// Authentication and Authorization Types

export interface IUser {
  readonly id: string;
  readonly login: string;
  readonly email: string;
  readonly fullName: string;
  readonly isActive: boolean;
  readonly isAdmin: boolean;
  readonly mustChangePassword: boolean;
  readonly lastPasswordChange: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ILoginRequest {
  identifier: string; // login or email
  password: string;
}

export interface IChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface ILoginResponse {
  accessToken: string;
  refreshToken: string;
  user: IUser;
  mustChangePassword: boolean;
}

export interface IRefreshTokenRequest {
  refreshToken: string;
}

/**
 * Ответ продления сессии.
 *
 * Продление ротирует обновление-токен: прежний становится недействительным,
 * дальше предъявляется только полученный здесь.
 */
export interface IRefreshResponse {
  accessToken: string;
  refreshToken: string;
}

export interface IRole {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly permissionsBitmask: number;
  readonly displayOrder: number;
  readonly createdAt: string;
}

export interface IProjectMember {
  readonly projectId: string;
  readonly userId: string;
  readonly userLogin: string;
  readonly userEmail: string;
  readonly userFullName: string;
  readonly roleId: string;
  readonly roleCode: string;
  readonly roleName: string;
  readonly joinedAt: string;
}

// Admin User Management Types

export interface IUpdateUserRequest {
  readonly login?: string;
  readonly email?: string;
  readonly fullName?: string;
  readonly isAdmin?: boolean;
  readonly isActive?: boolean;
}

export interface IResetPasswordRequest {
  readonly newPassword: string;
  readonly mustChangePassword: boolean;
}

export interface ICreateUserRequest {
  readonly login: string;
  readonly email: string;
  readonly fullName: string;
  readonly password: string;
  readonly isAdmin: boolean;
  readonly mustChangePassword: boolean;
}

/** SOURCE_DELETE permission bitmask (1 << 7) */
export const PERMISSION_SOURCE_DELETE = 128;

/**
 * PROJECT_EDIT (1 << 1) — им пользователь заявляет намерение редактировать,
 * и только оно даёт открыть сеанс редактирования
 * (spec/features/changelog-undo-redo/edit-sessions.md, R03, R11).
 */
export const PERMISSION_PROJECT_EDIT = 2;

/**
 * Права областей изменения — по ним история решает, какие области предлагать в
 * фильтре и какие записи вообще показывать
 * (spec/invariants/rbac.md, rbac-R34; change-history-R08, R17).
 *
 * Значения — те же биты, что в каталоге прав продукта.
 */
/** NOTES_VIEW (1 << 9) */
export const PERMISSION_NOTES_VIEW = 512;
/** NOTES_EDIT (1 << 10) */
export const PERMISSION_NOTES_EDIT = 1024;
/** DICTIONARIES_MANAGE (1 << 11) — единое право на просмотр и изменение (rbac-R02) */
export const PERMISSION_DICTIONARIES_MANAGE = 2048;
/** BM_VIEW (1 << 12) */
export const PERMISSION_BM_VIEW = 4096;
/** BM_EDIT (1 << 13) */
export const PERMISSION_BM_EDIT = 8192;
/** MAPPING_VIEW (1 << 14) */
export const PERMISSION_MAPPING_VIEW = 16384;
/** MAPPING_EDIT (1 << 15) */
export const PERMISSION_MAPPING_EDIT = 32768;
/** FEATURE_VIEW (1 << 16) — витрины живут под правом фичи (rbac-R03) */
export const PERMISSION_FEATURE_VIEW = 65536;
/** FEATURE_EDIT (1 << 17) */
export const PERMISSION_FEATURE_EDIT = 131072;

